/**
 * URLElicitationCard — SEP-1036 URL-mode elicitation with auto-poll completion.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { Lock as LockIcon, Language as LanguageIcon } from '@mui/icons-material'
import type { URLElicitationEntry } from '../types'
import { csrfHeaders } from '../utils/csrf'

interface URLElicitationCardProps {
    elicitations: URLElicitationEntry[]
    toolName?: string
    onComplete: () => void
    onCancel: () => void
}

type Status = 'idle' | 'opened' | 'completed' | 'expired' | 'cancelled'

const POLL_INITIAL_MS = 2000
const POLL_MAX_MS = 5000
const POLL_TIMEOUT_MS = 5 * 60 * 1000

function extractDomain(url: string): string {
    try {
        return new URL(url).hostname
    } catch {
        return url
    }
}

export const URLElicitationCard: React.FC<URLElicitationCardProps> = ({
    elicitations,
    toolName,
    onComplete,
    onCancel,
}) => {
    const elicitation = elicitations?.[0] ?? null
    const [status, setStatus] = useState<Status>('idle')
    const pollIntervalRef = useRef(POLL_INITIAL_MS)
    const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const startedAtRef = useRef(0)

    const domain = useMemo(
        () => (elicitation ? extractDomain(elicitation.url) : ''),
        [elicitation],
    )

    const stopPolling = () => {
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current)
            pollTimeoutRef.current = null
        }
    }

    const scheduleNext = () => {
        pollIntervalRef.current = Math.min(
            Math.round(pollIntervalRef.current * 1.25),
            POLL_MAX_MS,
        )
        pollTimeoutRef.current = setTimeout(() => void pollOnce(), pollIntervalRef.current)
    }

    const pollOnce = async () => {
        if (!elicitation) return
        if (Date.now() - startedAtRef.current > POLL_TIMEOUT_MS) {
            setStatus('expired')
            stopPolling()
            return
        }
        try {
            const resp = await fetch(
                `/api/mcp-gateway/api/elicitation/${encodeURIComponent(elicitation.elicitationId)}/status`,
                { method: 'GET', credentials: 'include', headers: { ...csrfHeaders() } },
            )
            if (!resp.ok) {
                scheduleNext()
                return
            }
            const data = await resp.json()
            if (data?.status === 'completed') {
                setStatus('completed')
                stopPolling()
                onComplete()
                return
            }
            scheduleNext()
        } catch {
            scheduleNext()
        }
    }

    const handleOpen = () => {
        if (!elicitation) return
        window.open(elicitation.url, '_blank', 'noopener,noreferrer')
        setStatus('opened')
        startedAtRef.current = Date.now()
        pollIntervalRef.current = POLL_INITIAL_MS
        pollTimeoutRef.current = setTimeout(() => void pollOnce(), POLL_INITIAL_MS)
    }

    const handleCancel = () => {
        stopPolling()
        setStatus('cancelled')
        onCancel()
    }

    useEffect(() => () => stopPolling(), [])

    if (!elicitation) {
        return (
            <Typography variant="body2" color="error">
                No elicitation data available
            </Typography>
        )
    }

    const message = elicitation.message || 'Authorization required to continue.'
    const buttonLabel =
        status === 'idle' ? 'Open secure link' : status === 'opened' ? 'Re-open link' : 'Done'

    return (
        <Box
            sx={{
                border: '1px solid var(--card-border, #e2e8f0)',
                borderRadius: 2,
                p: 2,
                bgcolor: 'var(--card-bg, #fff)',
                maxWidth: 480,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LockIcon sx={{ fontSize: 18, color: 'var(--main-accent, #3b82f6)' }} />
                <Typography variant="subtitle2" fontWeight={600}>
                    {toolName ? `Authorization required for ${toolName}` : 'Authorization required'}
                </Typography>
            </Box>

            <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.5 }}>
                {message}
            </Typography>

            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.25,
                    py: 0.5,
                    mb: 1.5,
                    borderRadius: 1,
                    bgcolor: 'var(--main-bg, #f0f4f8)',
                    fontFamily: 'monospace',
                    fontSize: 12,
                }}
                title="Destination domain — verify before continuing"
            >
                <LanguageIcon sx={{ fontSize: 14 }} />
                {domain}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleOpen}
                    disabled={status === 'completed' || status === 'cancelled'}
                >
                    {buttonLabel}
                </Button>
                {status !== 'completed' && status !== 'cancelled' && (
                    <Button variant="outlined" size="small" onClick={handleCancel}>
                        Cancel
                    </Button>
                )}
            </Box>

            {status === 'opened' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Waiting for completion in the new tab… the agent will resume automatically.
                </Typography>
            )}
            {status === 'completed' && (
                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                    Authorization complete. Resuming…
                </Typography>
            )}
            {status === 'expired' && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    The authorization link has expired. Cancel and retry.
                </Typography>
            )}
        </Box>
    )
}

export default URLElicitationCard
